import {
  SearchJobScopeOption,
  SearchJobsRequest,
} from '@taxfyle/api-internal/internal/job_pb'
import type { CursorPaged, CursorPageQuery } from '@/infra/paging'
import { mapSearchableJobProtoToProps } from '@/features/jobs/list/searchable-job-mappers'
import type {
  PrimeJobProgressionFn,
  PrimeLegendSettingsFn,
} from '@/features/jobs'
import { collection } from '@/infra/collection'
import type { GetCurrentWorkspaceFn, PrimeUserFn } from '@/features/iam'
import { SearchableJob } from '@/features/jobs/list/SearchableJob'
import type { JobsApi } from '@/features/jobs/api/job-api'
import {
  type Observable,
  concatMap,
  defer,
  map,
  mergeMap,
  mergeWith,
  repeat,
  catchError,
  EMPTY,
} from 'rxjs'
import { slidingBufferTime, filterNonNil } from '@/utils/rx-util'
import { share, take } from 'rxjs/operators'
import type { FetchLegendSettingsFn } from '@/features/jobs/internal'
import { isNotNil } from '@/utils/object-util'
import { retryOnRpcError } from '@/utils/grpc-util'

/**
 * Manages listing jobs via the search endpoint.
 */
export class SearchableJobStore {
  /**
   * Loaded jobs.
   */
  readonly jobs = collection({
    create: () => new SearchableJob(),
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
  })

  /**
   * Pipeline that, when subscribed to, will fetch jobs in the background
   * using the search endpoint. Useful to keep the data in the UI fresh
   * when the underlying job content is updated.
   */
  readonly refreshSearchableJobs$

  /**
   * Emits when a new job has been created and made available in the search index.
   */
  readonly newSearchableJob$

  constructor(
    private readonly _api: JobsApi,
    private readonly _getCurrentWorkspace: GetCurrentWorkspaceFn,
    private readonly _primeUser: PrimeUserFn,
    private readonly _fetchLegendSettings: FetchLegendSettingsFn,
    private readonly _primeLegendSettings: PrimeLegendSettingsFn,
    private readonly _primeJobProgression: PrimeJobProgressionFn,
    clear$: Observable<string>,
  ) {
    this.list = this.list.bind(this)

    this.refreshSearchableJobs$ = this._api.realtime$.pipe(
      map(
        (e) =>
          e.getJobCreated()?.getJob()?.getId() ??
          e.getJobUpdated()?.getJob()?.getId() ??
          e.getJobProgression()?.getJobId() ??
          e.getRequestedSupportingItemUpdated()?.getJobId() ??
          e.getBookkeepingUpdated()?.getProgressionInfo()?.getId(),
      ),
      mergeWith(clear$),
      filterNonNil(),
      // Batch for a little bit. This is both to ensure minimum requests to
      // the backend and account for eventual consistency with
      // the search index.
      slidingBufferTime({ slide: 1500, maxWait: 2000, count: 50 }),
      concatMap(this._refreshSearchableJobs.bind(this)),
      share(),
    )

    this.newSearchableJob$ = this._api.realtime$.pipe(
      map((e) => e.getJobCreated()?.getJob()?.getId()),
      filterNonNil(),
      mergeMap((id) =>
        // Polls the job through the search endpoint a
        // few times until it's returned,
        defer(() => this._searchForNewlyCreatedJob(id)).pipe(
          catchError(() => EMPTY),
          filterNonNil(),
          repeat({ delay: 1_000, count: 10 }),
          take(1),
        ),
      ),
      share(),
    )
    // Always subscribe to this, otherwise
    // a consumer may not subscribe in time to catch the event that starts
    // the polling.
    this.newSearchableJob$.subscribe()
  }

  private async _searchForNewlyCreatedJob(id: string) {
    const result = await this.list({
      filter: `id="${id}"`,
      limit: 1,
      after: null,
    })

    if (result.data.length) {
      return result.data[0]
    }

    return null
  }

  /**
   * Lists searchable jobs.
   */
  async list(
    query: ListSearchableJobsQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<SearchableJob>> {
    signal?.throwIfAborted()

    const req = new SearchJobsRequest()

    if (query.limit) {
      req.setPageSize(query.limit)
    }
    if (query.after) {
      req.setPageToken(query.after)
    }

    if (query.orderBy) {
      req.setOrderBy(query.orderBy)
    }

    if (query.filter) {
      req.setFilter(query.filter)
    }

    req.setScope(SearchJobScopeOption.MINE)

    const response = await retryOnRpcError(
      () => this._api.jobs.searchJobs(req),
      { signal },
    )

    // Make sure the legend settings have been fetched before returning the
    // response to avoid showing jobs where we don't yet know their service type.
    // This is because the searchable job doesn't include the service type.
    await Promise.all(
      response
        .getJobsList()
        .map((proto) =>
          this._fetchLegendSettings(
            proto.getLegendId(),
            proto.getLegendVersion(),
          ),
        ),
    )

    const jobs = response
      .getJobsList()
      .map((x) =>
        mapSearchableJobProtoToProps(
          x,
          this._primeUser,
          this._primeLegendSettings,
          this._primeJobProgression,
        ),
      )

    return {
      data: this.jobs.set(jobs),
      total: response.getTotalSize(),
      next: response.getNextPageToken(),
    }
  }

  /**
   * Refreshes the given job IDs by querying for them using a filter.
   */
  private async _refreshSearchableJobs(ids: string[]) {
    // Filter out IDs for:
    // - jobs we haven't loaded
    // - jobs that are not in the current workspace
    const currentWorkspace = this._getCurrentWorkspace()
    const jobIds = ids
      .map((id) => {
        const job = this.jobs.get(id)
        if (!job) {
          return null
        }

        if (job.workspaceId !== currentWorkspace.id) {
          return null
        }

        return job.id
      })
      .filter(isNotNil)

    if (!jobIds.length) {
      // Nothing to refresh
      return
    }

    const filter = jobIds.map((id) => `id="${id}"`).join(' OR ')
    await this.list({
      filter,
      limit: ids.length,
      after: null,
    })
  }
}

/**
 * The query parameters for the {@link SearchableJobStore.list} method.
 */
export interface ListSearchableJobsQuery extends CursorPageQuery {
  orderBy?: string
  filter?: string
}
